/**
 * The app installed on a phone runs full screen, with no address bar and no
 * pull to refresh, and the phone keeps it alive in the background for days:
 * an old page never asks for the new code. So the page asks the site which
 * build it serves (`version.json`, written by the build, vite.config.ts) when
 * it comes back to the foreground and every few minutes, and reloads on a new
 * one as soon as no match is being played. A reload inside a room takes back
 * the same seat (resume token).
 */

/** This page's build; the same value is in the `version.json` it came with. */
const BUILD = String(import.meta.env.VITE_BUILD_ID ?? "");
const CHECK_EVERY_MS = 10 * 60 * 1000;
const RETRY_MS = 2000;
/** What the last reload was for: if the page that comes back still is not it (a stale cache on the way), no loop. */
const RELOADED_FOR = "arena-reloaded-for";

/** `safe`: no match is being played. `before`: tells the player, just before reloading. */
export function watchForUpdates(safe: () => boolean, before: () => void): void {
  if (import.meta.env.DEV || !BUILD) return;
  let pending = false;
  const check = async (): Promise<void> => {
    if (pending || document.hidden) return;
    const served = await servedBuild();
    if (!served || served === BUILD || pending) return;
    pending = true;
    const timer = window.setInterval(() => {
      if (!safe()) return;
      window.clearInterval(timer);
      if (!reloadOnce(served, before)) pending = false;
    }, RETRY_MS);
  };
  document.addEventListener("visibilitychange", () => void check());
  window.addEventListener("pageshow", (event) => { if (event.persisted) void check(); });
  window.setInterval(() => void check(), CHECK_EVERY_MS);
  void check();
}

/** The server refused this page as too old: one reload fetches the new one. False if that was already tried. */
export function reloadOutdated(before: () => void): boolean {
  return reloadOnce(`outdated ${BUILD}`, before);
}

function reloadOnce(reason: string, before: () => void): boolean {
  try {
    if (sessionStorage.getItem(RELOADED_FOR) === reason) return false;
    sessionStorage.setItem(RELOADED_FOR, reason);
  } catch {
    // Without storage a loop cannot be ruled out: better an old page than a page that never stops reloading.
    return false;
  }
  before();
  location.reload();
  return true;
}

async function servedBuild(): Promise<string | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!response.ok) return null;
    const { build } = (await response.json()) as { build?: unknown };
    return typeof build === "string" && build !== "" ? build : null;
  } catch {
    return null;
  }
}
